import React, { useRef, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import './Hero.css';

const Hero = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  // State to track if screen is wider than 1200px
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 1200);
  
  // Update isWideScreen state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth > 1200);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleSmoothScroll = (e, href) => {
    e.preventDefault();
    if (href === '#statistics-grid') {
      const target = document.getElementById('statistics-grid');
      if (target) {
        target.scrollIntoView({
          behavior: isWideScreen ? 'smooth' : 'auto',
          block: 'start',
        });
      }
      return;
    }
    if (href === '#') {
      window.scrollTo({ 
        top: 0, 
        behavior: isWideScreen ? 'smooth' : 'auto' 
      });
      return;
    }
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({
        behavior: isWideScreen ? 'smooth' : 'auto',
        block: 'start'
      });
    }
  };

  return (
    <section className="genix-hero">
      <div className="genix-container">
        <div className="genix-hero-content">
          <div className={`genix-hero-text ${isHomePage ? 'home-hero-text' : ''}`}>
            <h1>Simplify Clinical Operations With AI-Driven Solutions</h1>
            
            <div className="genix-hero-buttons">
              <a href="#statistics-grid" className="genix-btn genix-btn-outline" onClick={(e) => handleSmoothScroll(e, '#statistics-grid')}>
                Learn More →
              </a>
              <Link to="/book-demo" className="genix-btn genix-btn-primary" style={{left:'0px'}}>
                Book a Demo →
              </Link>
            </div>
            
            <ul className="genix-hero-features">
              <li>
                <span className="genix-check-icon">✓</span>
                Real-time transcription during consultations
              </li>
              <li>
                <span className="genix-check-icon">✓</span>
                Multilingual translation in 20+ languages
              </li>
              <li>
                <span className="genix-check-icon">✓</span>
                Electronic Health Record integration
              </li>
            </ul>
          </div>
          
          <div className="genix-hero-image">
            <img 
              src="/images/Herosecimg.gif" 
              alt="Medical professional in surgical attire" 
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const heroVariants = {
  hidden: { opacity: 0, y: 80, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
    transition: {
      duration: 1.1,
      ease: [0.22, 1, 0.36, 1],
      when: 'beforeChildren',
      staggerChildren: 0.18,
    },
  },
};

const childVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export const ProductHero = ({ title, description, imageSrc, imageAlt }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-20% 0px -20% 0px' });
  const location = useLocation();
  const isGenixHr = location.pathname === '/products/genix-hr';
  const isGenixPendant = location.pathname === '/products/genix-pendent';
  const isGenixRoata = location.pathname === '/products/genix-roata';
  // State to track if screen is wider than 1200px
  const [isWideScreen, setIsWideScreen] = useState(window.innerWidth > 1200);
  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth > 1200);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Smooth scroll handler for product pages
  const handleProductScroll = (e) => {
    e.preventDefault();
    let targetId = '';
    if (isGenixHr) targetId = 'product-stats-section';
    else if (isGenixPendant) targetId = 'device-overview-section';
    else if (isGenixRoata) targetId = 'key-use-cases-roata-section';
    if (targetId) {
      const target = document.getElementById(targetId);
      if (target) {
        target.scrollIntoView({
          behavior: isWideScreen ? 'smooth' : 'auto',
          block: 'start',
        });
      }
    }
  };

  return (
    <motion.section
      className={`genix-hero${isGenixHr ? ' genix-hr-hero' : ''}`}
      ref={ref}
      variants={heroVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="genix-container">
        <div className="genix-hero-content">
          <motion.div className="genix-hero-text" variants={childVariants}>
            <motion.h1 variants={childVariants}>{title}</motion.h1>
            <motion.p variants={childVariants}>{description}</motion.p>
            <motion.div className="genix-hero-buttons" variants={childVariants}>
              <a
                href={isGenixHr ? "#product-stats-section" : isGenixPendant ? "#device-overview-section" : isGenixRoata ? "#key-use-cases-roata-section" : "#"}
                className="genix-btn genix-btn-outline"
                onClick={handleProductScroll}
              >
                Learn More ↗
              </a>
              <Link to="/book-demo" className="genix-btn genix-btn-primary" style={{left:'0'}}>
                Book a Demo ↗
              </Link>
            </motion.div>
          </motion.div>
          <motion.div className="genix-hero-image" variants={childVariants}>
            <img 
              src={imageSrc} 
              alt={imageAlt} 
              style={{
                width: '100%',
                height: 'auto',
              }}
            />
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default Hero;
